import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Popover, Tooltip } from 'antd';
import isUndefined from 'lodash/isUndefined';
import {
  renderNumber,
  creatingArrayWithNumber,
  getNights,
  getReserveUnitUrl,
  getFullUnitName,
  formatDateCheckInOut,
} from 'src/utils/index';
import { InfoSvg, ManDarkSvg, ErrorWarningSvg, BathSvg, KitchenSvg } from 'src/assets/index';
import { ICondoUnit, CondoMatchTypeEnum, IDetails, RefundableEnum } from 'src/common-types/index';
import { DATE_FORMAT_COMA } from 'src/constants/index';
import {
  BlueButton,
  ModalCancellationPolicies,
  WhiteButton,
} from 'src/components/components/index';
import { ModalUnitDetails } from '../modal-unit-details';
import './style.scss';

interface IProps extends WrappedComponentProps {
  index: number;
  unit: ICondoUnit;
  condo: IDetails;
  condoId: number;
  matchType: string;
  isOnlyAlternative: boolean;
  isAnytime?: boolean;
  isSpecificDates?: boolean;
  getCurrentUnit: (value: string, index: number) => void;
  currentUnitName: string;
  isEmptyAlternativeUnit: boolean;
  quantityAlternativeUnit: number;
  travelDates?: { startDate: string; endDate: string };
}

interface IState {
  isModalVisible: boolean;
  isModalCancellationPoliciesVisible: boolean;
  isChangedCancellationPolicy: boolean;
}

const ZERO = 0;
const ONE = 1;

export class UnitComponent extends React.Component<IProps, IState> {
  state: IState = {
    isModalVisible: false,
    isModalCancellationPoliciesVisible: false,
    isChangedCancellationPolicy: false,
  };

  handleCancel = (): void => {
    this.setState({ isModalVisible: !this.state.isModalVisible });
  };

  showCancellationPolicies = (): void => {
    this.setState({
      isModalCancellationPoliciesVisible: true,
    });
  };

  handleCancelModalCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: false });
  };

  onClickView = (): void => {
    const { getCurrentUnit, unit, intl, index } = this.props;

    getCurrentUnit(getFullUnitName(unit, intl), index);
  };

  alternativeBlockInfo = (): React.ReactNode => {
    return (
      <div className="units-search-unit__available_dates-block">
        <InfoSvg />
        <p className="units-search-unit__available_dates-text">
          <FormattedMessage id="available.on.alternative.dates" />
        </p>
        <div className="units-search-unit__available_dates-btn-block">
          <WhiteButton onClick={this.onClickView}>
            <FormattedMessage id="view.title" />
          </WhiteButton>
        </div>
      </div>
    );
  };

  formatDate = (date: string): string => {
    return formatDateCheckInOut(date, DATE_FORMAT_COMA);
  };

  componentDidMount(): void {
    const { getCurrentUnit, unit, intl, index } = this.props;

    if (index === ZERO) {
      getCurrentUnit(getFullUnitName(unit, intl), index);
    }
  }

  render(): React.ReactNode {
    const { isModalVisible, isModalCancellationPoliciesVisible, isChangedCancellationPolicy } =
      this.state;
    const {
      condo,
      unit,
      condoId,
      matchType,
      isSpecificDates = false,
      currentUnitName,
      intl,
      isEmptyAlternativeUnit,
      quantityAlternativeUnit,
      travelDates,
    } = this.props;
    const {
      maxOccupancy,
      pricePerNight,
      price,
      dateRange,
      unitId,
      privacyOccupancy,
      bathroom,
      kitchenType,
      cancellationPolicyText,
      refundability,
      name,
      supplierIdentifier,
    } = unit;

    return (
      <div
        className={`units-search-unit ${currentUnitName === getFullUnitName(unit, intl) && quantityAlternativeUnit !== ZERO
          ? 'active-unit'
          : ''
          }`}
      >
        <div className="units-search-unit__top-wrapper">
          <div className="units-search-unit__info-items">
            <Tooltip title={name}>
              <h4 className="units-search-unit__info-title capitalize">{name}</h4>
            </Tooltip>
            {matchType === CondoMatchTypeEnum.Exact && !isSpecificDates ? (
              <div className="units-search-unit__label-info">
                <p className="units-search-unit__label-price">${renderNumber(pricePerNight)}</p>
                <p className="units-search-unit__label-text">
                  <FormattedMessage id="per.night" />
                </p>
              </div>
            ) : null}
          </div>
          <div className="units-search-unit__guests">
            {creatingArrayWithNumber(maxOccupancy, ONE).map((value, i) => {
              return (
                <span className="units-search-unit__icon-wrapper" key={i}>
                  <ManDarkSvg />
                </span>
              );
            })}
          </div>
          <div className="units-search-unit__capacity">
            <div className="units-search-unit__capacity-text">
              <FormattedMessage id="max.sleeps" />: {maxOccupancy}
            </div>
            <div className="units-search-unit__capacity-text">
              <FormattedMessage id="private" />: {privacyOccupancy}
            </div>
            <div className="units-search-unit__capacity-text">{supplierIdentifier}</div>
          </div>
          <div className="units-search-unit__wrapper-block-info">
            {!isUndefined(bathroom) && (
              <div className="units-search-unit__block-info">
                <BathSvg />
                <p className="units-search-unit__block-info-text">{bathroom}</p>
              </div>
            )}
            {!isUndefined(kitchenType) && (
              <div className="units-search-unit__block-info">
                <KitchenSvg />
                <p className="units-search-unit__block-info-text capitalize">{kitchenType}</p>
              </div>
            )}
            <div className="units-search-unit__unit-details-wrapper">
              <p className="units-search-unit__unit-details-text" onClick={this.handleCancel}>
                <FormattedMessage id="more.info" />
              </p>
              <ModalUnitDetails
                condo={condo}
                unit={unit}
                visible={isModalVisible}
                onCancel={this.handleCancel}
              />
            </div>
          </div>
        </div>
        {matchType === CondoMatchTypeEnum.Exact && !isSpecificDates ? (
          <>
            <div className="units-search-unit__bottom-wrapper">
              <div className="units-search-unit__refundable">
                {refundability === RefundableEnum.Nonrefundable && (
                  <div
                    className="units-search-unit__refundable-nonref"
                    onClick={this.showCancellationPolicies}
                  >
                    <Popover
                      overlayClassName="overlay-popover"
                      content={cancellationPolicyText}
                      visible={isChangedCancellationPolicy}
                      placement="bottomLeft"
                    >
                      <FormattedMessage id="non.refundable" />
                      <InfoSvg />
                    </Popover>
                  </div>
                )}
                {refundability === RefundableEnum.Refundable && (
                  <div
                    className="units-search-unit__refundable-ref"
                    onClick={this.showCancellationPolicies}
                  >
                    <Popover
                      overlayClassName="overlay-popover"
                      content={cancellationPolicyText}
                      visible={isChangedCancellationPolicy}
                      placement="bottomLeft"
                    >
                      <FormattedMessage id="refundable" />
                      <InfoSvg />
                    </Popover>
                  </div>
                )}
              </div>
              <div className="units-search-unit__bottom">
                <div className="units-search-unit__price">
                  <div className="units-search-unit__price-total">
                    <p className="units-search-unit__price-total-text">
                      ${renderNumber(Math.floor(price))}
                    </p>
                    <div className="units-search-unit__for-info">
                      <p className="units-search-unit__for-info-text">
                        <FormattedMessage id="for" />{' '}
                        <FormattedMessage
                          id="nights"
                          values={{ count: getNights(dateRange.from, dateRange.to) }}
                        />{' '}
                        <span className="units-search-unit__price-includes-taxes">
                          <FormattedMessage id="abbreviated.price.includes.taxes" />
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="units-search-unit__action">
                  <Link to={getReserveUnitUrl(condoId, unitId)}>
                    <BlueButton>
                      <FormattedMessage id="reserve" />
                    </BlueButton>
                  </Link>
                </div>
              </div>
              {travelDates ? (
                <div className="units-search-unit__dates">
                  <FormattedMessage id="travel.dates" />:{' '}
                  <span className="units-search-unit__dates-bold">
                    {this.formatDate(travelDates.startDate)} -{' '}
                    {this.formatDate(travelDates.endDate)}
                  </span>
                </div>
              ) : null}
            </div>
            {isEmptyAlternativeUnit ? (
              <div className="units-search-unit__available_dates-block">
                <InfoSvg />
                <p className="units-search-unit__available_dates-text">
                  <FormattedMessage id="available.on.alternative.dates" />
                </p>
                <div className="units-search-unit__available_dates-btn-block">
                  <WhiteButton onClick={this.onClickView}>
                    <FormattedMessage id="view.title" />
                  </WhiteButton>
                </div>
              </div>
            ) : null}
          </>
        ) : matchType === CondoMatchTypeEnum.Alternative && !isSpecificDates ? (
          this.alternativeBlockInfo()
        ) : (
          <div className="units-search-unit__alternative-dates specify-dates">
            <ErrorWarningSvg />
            <span>
              <FormattedMessage id="specify.travel.dates.unit" />
            </span>
          </div>
        )}
        <ModalCancellationPolicies
          isForCondo={true}
          cancellationPoliciesText={cancellationPolicyText}
          visible={isModalCancellationPoliciesVisible}
          onCancel={this.handleCancelModalCancellationPolicies}
        />
      </div>
    );
  }
}

export const Unit = injectIntl(UnitComponent);

